import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PortfolioSection() {
  const [hoveredProject, setHoveredProject] = useState(null);
  const navigate = useNavigate();

  const scrollToSection = () => {
    navigate('/about');
    
    // Try to scroll after increasing delays
    const scrollAttempt = (delay) => {
      setTimeout(() => {
        const element = document.getElementById('selected-work-highlights');
        if (element) {
          window.scrollTo({ top: element.offsetTop - 100, behavior: 'smooth' });
        }
      }, delay);
    };
    
    // Try scrolling after 300ms, 600ms, and 1000ms to ensure page is loaded
    scrollAttempt(300);
    scrollAttempt(600);
    scrollAttempt(1000);
  };

  const projects = [
    {
      id: 1,
      image: '/img/cs.png',
      tags: ['Education', 'Web Platform'],
      title: 'Smart Online Learning Platform',
      description: 'A scalable e-learning platform offering live classes, course management, and AI-powered personalized learning.',
      link: 'https://www.casuccessful.com/'
    },
    {
      id: 2,
      image: '/img/car.png',
      tags: ['Transport', 'Mobile App'],
      title: 'Apna Safar Bhopal',
      description: 'A reliable taxi service platform offering quick bookings, real-time tracking, and safe, comfortable rides across the city.',
      link: 'https://apnasafarbhopal.in/',
      reverse: true
    },
    {
      id: 3,
      image: '/img/bhaiya.png',
      tags: ['Portfolio', 'Web Design'],
      title: 'Personal Portfolio Website',
      description: 'A modern portfolio website showcasing skills, projects, and achievements with a clean and professional design.',
      link: 'https://rajeshpatelsironj.com/'
    }
  ];

  return (
    <section className="relative bg-gray-950 py-24 lg:py-32 overflow-hidden border-b border-gray-800">
      {/* Background Abstract Glows */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px] pointer-events-none z-0"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/5 rounded-full blur-[150px] pointer-events-none z-0"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-24">
          <div className="inline-flex items-center border border-gray-700 px-4 py-2 bg-gray-800/50 backdrop-blur-sm text-xs font-bold uppercase tracking-widest text-amber-400 mb-6 rounded-md shadow-sm">
            <span className="w-2 h-2 bg-amber-400 rounded-full mr-3 animate-pulse shadow-[0_0_8px_rgba(251,191,36,0.8)]"></span>
            Featured Portfolio
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight uppercase mb-6 text-white">
            Delivering <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Digital Excellence</span>
          </h2>
          
          <p className="text-gray-400 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Explore some of our most impactful projects where innovative engineering meets strategic business goals.
          </p>
        </div>

        {/* Projects List (Zig-Zag Layout) */}
        <div className="space-y-20 lg:space-y-32">
          {projects.map((project, idx) => (
            <div
              key={project.id}
              className={`group flex flex-col ${project.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 lg:gap-16`}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {/* Image */}
              <div className="w-full lg:w-3/5 relative rounded-[2rem] overflow-hidden shadow-2xl border border-gray-800 group-hover:border-amber-500/40 transition-all duration-500 z-10 bg-gray-900">
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent z-10 pointer-events-none"></div>
                <img 
                  src={project.image}
                  alt={`Screenshot of ${project.title} project`}
                  className="w-full aspect-video object-cover transform transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100"
                />
              </div>
              
              {/* Content */}
              <div className={`w-full lg:w-2/5 flex flex-col justify-center relative z-20 ${project.reverse ? 'lg:text-right lg:items-end' : ''}`}>
                {/* Tags */}
                <div className={`flex flex-wrap ${project.reverse ? 'justify-start lg:justify-end' : 'justify-start'} gap-2 mb-5`}>
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 bg-gray-800 border border-gray-700 text-gray-300 text-[10px] font-bold uppercase tracking-wider rounded-lg"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Title */}
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight group-hover:text-amber-400 transition-colors">
                  {project.title}
                </h3>

                {/* Description */}
                <p className={`text-gray-400 text-sm sm:text-base leading-relaxed mb-8 ${project.reverse ? 'text-left lg:text-right' : ''}`}>
                  {project.description}
                </p>

                {/* CTA Button */}
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center ${project.reverse ? 'lg:flex-row-reverse' : ''} gap-3 text-amber-500 font-bold uppercase tracking-widest text-xs sm:text-sm hover:text-amber-400 transition-colors w-fit group-hover:translate-x-1 ${project.reverse ? 'lg:group-hover:-translate-x-1' : ''} transition-transform`}
                >
                  Check It Out
                  <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center border border-amber-500/30 group-hover:bg-amber-500 group-hover:text-black transition-all duration-300">
                    <svg className={`w-4 h-4 transform transition-transform group-hover:${project.reverse ? '-' : ''}translate-x-1 ${project.reverse ? 'lg:rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                    </svg>
                  </div>
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="mt-20 text-center">
          <button
            onClick={scrollToSection}
            className="inline-flex items-center justify-center px-8 py-4 border border-gray-700 text-white font-bold uppercase tracking-widest text-xs sm:text-sm rounded-xl hover:bg-amber-500 hover:text-black hover:border-amber-500 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_rgba(245,158,11,0.5)] cursor-pointer"
          >
            Explore All Projects
          </button>
        </div>
      </div>
    </section>
  );
}
